import { Button } from '@alifd/next';
import { Box } from '@mui/material';
import { Cascader, DatePicker, Form, Modal, Select, Space, Table } from 'antd';
import locale from 'antd/es/date-picker/locale/zh_TW';
import axios from 'axios';
import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import PageHeader from '@/components/PageHeader';
import DownloadButton from '@/components/UI/DownloadButton';
import useService from '@/lib/hooks/useService';
import type { RootState } from '@/redux/store';
import type {
  ChargingPileExpenseListParams as Params,
  ChargingPileExpenseListResponseData,
  CountingResponseData,
  MonthlyAndDailyResponseData,
} from '@/types/api/chargingPile/getChargingPileExpenseList';
import type { ChargingPile } from '@/types/database/chargingPile/chargingPile';
import type { ChargingPileElectricityRate } from '@/types/database/chargingPile/chargingPileElectricityRate';
import {
  generateHouseHoldNumberCascader,
  type Option,
} from '@/utils/generateHouseHoldNumberCascader';

import {
  checkIsSuperAdmin,
  confirmChangePricingStandard,
  getExpense,
  getFormItems,
  getPricingUnitPrice,
} from './helper';
import styles from './index.module.scss';
import {
  type ActionColumnType,
  countingColumns,
  type CountingColumnsType,
  monthlyAndDailyColumns,
  type MonthlyAndDailyColumnsType,
} from './tableColumnsSetting';

const { Item, useForm } = Form;

const ChargingPileExpenseList: React.FC = () => {
  const [editForm] = useForm();

  const userData = useSelector((state: RootState) => state.user);
  const userAuth = useMemo(
    () => userData?.auth?.user?.auth,
    [userData?.auth?.user?.auth]
  );

  const [householdCascader, setHouseholdCascader] = useState<Option[]>([]);
  const [parkingOptions, setParkingOptions] = useState<Option[]>([]);
  const [selectedHouse, setSelectedHouse] = useState<string>();
  const [selectedParking, setSelectedParking] = useState<string>();
  const [dateRange, setDateRange] = useState<[string, string]>([
    moment().startOf('month').format('YYYY-MM-DD'),
    moment().format('YYYY-MM-DD'),
  ]);
  const [countingType, setCountingType] = useState<
    'daily' | 'counting' | 'monthly'
  >('counting');
  const [dataSource, setDataSource] = useState<
    ((CountingResponseData | MonthlyAndDailyResponseData) & {
      key: string;
    })[]
  >([]);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isOpenEditPriceModal, setIsOpenEditPriceModal] = useState(false);
  const [isNeedUpdatePage, setIsNeedUpdatePage] = useState(false);

  const [piles, , pileService] = useService<ChargingPile>({
    collectionName: 'chargingPile',
  });
  const [chargingPileElectricityRate] = useService<ChargingPileElectricityRate>(
    {
      collectionName: 'chargingPileElectricityRate',
    }
  );

  const handleIsNeedUpdatePage = (isNeedUpdatePage: boolean) => {
    setIsNeedUpdatePage(isNeedUpdatePage);
  };

  useEffect(() => {
    async () => {
      const promises: Promise<void>[] = [];
      for (let i = 1; i < 10; i++) {
        for (let j = 1; j < 10; j++) {
          promises.push(
            pileService.create({
              chargingPileID: `${i}${j}`,
              householdNumber: `${i}-${j}`,
              parkingNumber: `${i}-${j}`,
            })
          );
        }
      }
      await Promise.all(promises);
    };
  }, [pileService]);

  const getParams = useCallback((): Params => {
    const params: Params = {
      entityType: 'house',
      dateRange,
      countingType,
    };
    if (selectedParking) {
      params.entityType = 'parking';
      params.entityNumber = [selectedParking];
    } else if (!selectedParking && selectedHouse) {
      params.entityNumber = [selectedHouse];
    }
    return params;
  }, [countingType, dateRange, selectedHouse, selectedParking]);

  const getName = useCallback((): string => {
    const type = {
      daily: '(以每日充電量計算)',
      monthly: '(以每月充電量計算)',
      counting: '(以充電次數計算)',
    };
    const time = dateRange.join('到');
    const entity = selectedParking ? '車位' : '住戶';
    let object = '';
    if (selectedParking) {
      object = selectedParking;
    } else {
      if (selectedHouse) {
        object = selectedHouse;
      }
    }
    return `${time}${entity}${object}充電樁電費錶${type[countingType]}.xlsx`;
  }, [countingType, dateRange, selectedHouse, selectedParking]);

  const handleClickEditButton = useCallback(
    (record) => {
      editForm.setFieldsValue({
        ...record,
      });
      setIsOpenEditPriceModal(true);
    },
    [editForm]
  );

  useEffect(() => {
    const getDataSource = async () => {
      try {
        const res = await axios.get('/ezcon/api/excelDataToRender', {
          headers: {
            authorization: localStorage.getItem('feathers-jwt'),
          },
          params: getParams(),
        });
        return res.data;
      } catch (err) {
        console.error(err);
      }
    };
    (async () => {
      setDataSource([]);
      const data: ChargingPileExpenseListResponseData = await getDataSource();
      if (data) {
        setDataSource(
          data.map((d, index) => ({ ...d, key: index.toString() }))
        );
      }
      if (isNeedUpdatePage) {
        setIsNeedUpdatePage(false);
      }
    })();
  }, [getParams, isNeedUpdatePage]);

  useEffect(() => {
    if (piles.length > 0) {
      const householdNumbers: string[] = [];
      piles.forEach((pile) => {
        if (typeof pile.householdNumber === 'string') {
          householdNumbers.push(pile.householdNumber);
        }
      });
      setHouseholdCascader(generateHouseHoldNumberCascader(householdNumbers));
    }
  }, [piles]);

  useEffect(() => {
    if (piles.length > 0) {
      const parkingNumbers: string[] = [];
      piles
        .filter((pile) =>
          selectedHouse ? pile.householdNumber === selectedHouse : true
        )
        .forEach((pile) => {
          if (typeof pile.parkingNumber === 'string') {
            parkingNumbers.push(pile.parkingNumber);
          }
        });
      setParkingOptions(generateHouseHoldNumberCascader(parkingNumbers));
    }
  }, [selectedHouse, piles]);

  const closeEditModal = () => {
    editForm.resetFields();
    setIsOpenEditPriceModal(false);
  };

  const tableColumns = useMemo(() => {
    const columns: CountingColumnsType | MonthlyAndDailyColumnsType =
      countingType === 'counting'
        ? [...countingColumns]
        : [...monthlyAndDailyColumns];

    const actionColumn: ActionColumnType = {
      title: '操作',
      dataIndex: 'action',
      fixed: 'right',
      render: (_, record) => {
        return (
          <div>
            <Button type="normal" onClick={() => handleClickEditButton(record)}>
              編輯
            </Button>
          </div>
        );
      },
    };
    if (countingType === 'counting' && isEditMode) {
      columns.push(actionColumn);
    }

    return columns;
  }, [countingType, handleClickEditButton, isEditMode]);

  const handleEditFormChange = (changedValues: CountingResponseData) => {
    if ('pricingStandard' in changedValues) {
      const pricingUnitPrice = getPricingUnitPrice(
        changedValues.pricingStandard,
        chargingPileElectricityRate
      );
      const expense = getExpense(
        pricingUnitPrice,
        editForm.getFieldValue('usage')
      );
      editForm.setFieldsValue({
        ...editForm.getFieldsValue(),
        pricingUnitPrice,
        expense,
      });
    }
  };

  const handleSelectPricingChange = (
    value: 'daily' | 'counting' | 'monthly'
  ) => {
    if (isEditMode) {
      setIsEditMode(false);
    }
    setCountingType(value);
  };

  return (
    <Box>
      {/* 標題 */}
      <PageHeader
        title="充電樁歷史紀錄"
        breadcrumbs={[{ name: '汽車充電樁' }, { name: '充電樁歷史紀錄' }]}
      />
      <Modal
        open={isOpenEditPriceModal}
        footer={null}
        onCancel={closeEditModal}
      >
        <Form
          form={editForm}
          onFinish={(formValues: CountingResponseData) =>
            confirmChangePricingStandard({
              formValues,
              closeEditModal,
              handleIsNeedUpdatePage,
            })
          }
          onValuesChange={handleEditFormChange}
        >
          {getFormItems().map((itemElement) => itemElement)}
          <Item>
            <Space className={styles.formFooterButtons}>
              <Button type="primary" htmlType="submit">
                確定
              </Button>
              <Button onClick={closeEditModal}>取消</Button>
            </Space>
          </Item>
        </Form>
      </Modal>

      {/* 功能區 */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          gap: '10px',
          flexWrap: 'wrap',
        }}
      >
        {/* filter */}
        <Cascader
          style={{ width: 275 }}
          value={selectedHouse?.split('-')}
          onChange={(val) => {
            setSelectedParking(undefined);
            setSelectedHouse(val?.join('-'));
          }}
          options={householdCascader}
          placeholder={
            householdCascader.length > 0
              ? '請選擇戶號或留白以檢視全部'
              : '資料讀取中'
          }
        />
        <Cascader
          style={{ width: 275 }}
          options={parkingOptions}
          value={selectedParking?.split('-')}
          onChange={(val) => setSelectedParking(val?.join('-'))}
          placeholder={
            parkingOptions.length > 0
              ? '請選擇車位編號或留白以檢視全部'
              : '資料讀取中'
          }
          allowClear
        />
        <Box
          sx={{
            width: 'fit-content',
            display: 'flex',
            flexDirection: 'row',
            gap: '10px',
          }}
        >
          <DatePicker.RangePicker
            locale={locale}
            picker={countingType === 'monthly' ? 'month' : 'date'}
            value={
              (dateRange?.map((item) => moment(item)) as [
                moment.Moment,
                moment.Moment
              ]) || []
            }
            onChange={function handleDateRangeChange(_, dateString) {
              setDateRange(dateString);
            }}
            allowClear={false}
          />
          <Button
            onClick={() =>
              setDateRange([
                moment().startOf('M').format('YYYY-MM-DD'),
                moment().format('YYYY-MM-DD'),
              ])
            }
          >
            本月
          </Button>
          <Button
            onClick={() =>
              setDateRange([
                moment().subtract(1, 'M').startOf('M').format('YYYY-MM-DD'),
                moment().subtract(1, 'M').endOf('M').format('YYYY-MM-DD'),
              ])
            }
          >
            上月
          </Button>
        </Box>

        <Select
          style={{ width: 200 }}
          placeholder="請選擇計算方式"
          value={countingType}
          onChange={handleSelectPricingChange}
        >
          <Select.Option value="counting">以每次充電量計算</Select.Option>
          <Select.Option value="daily">以每日充電量計算</Select.Option>
          <Select.Option value="monthly">以每月充電量計算</Select.Option>
        </Select>
        {/* download */}
        <DownloadButton
          {...{
            url: '/ezcon/api/downloadChargingPileExpenseExcel',
            params: getParams(),
            fileName: getName(),
            responseFileType: 'json',
          }}
        >
          下載當前資料
        </DownloadButton>
        {checkIsSuperAdmin(userAuth) && (
          <Button type="normal" onClick={() => setIsEditMode(true)}>
            啟用編輯
          </Button>
        )}
      </Box>
      {/* 列表 */}
      <Box
        sx={{
          padding: '10px',
          borderRadius: '5px',
          backgroundColor: 'white',
          marginTop: '10px',
        }}
      >
        <Table
          bordered
          columns={tableColumns}
          dataSource={dataSource}
          showSorterTooltip={false}
          scroll={{ x: 1400, scrollToFirstRowOnChange: true }}
          pagination={{
            defaultPageSize: 10,
          }}
          sortDirections={['descend', 'ascend', 'descend']}
        />
      </Box>
    </Box>
  );
};

export default ChargingPileExpenseList;
